import { Link } from '@tanstack/react-router'
import { ArchiveRestoreIcon, ArrowLeftIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { Button, DataTable } from '@/components/ui'
import type { Row, Table } from '@/components/ui/table/types'
import { useAccount } from '@/modules/account'
import {
  type Project,
  useArchivedProjects,
  useFetchArchivedProjects,
  useLoadingArchivedProjects,
} from '@/modules/projects'
import { useDialogActions } from '@/stores/dialog'
import { isProjectDeletable } from '../expiration'
import { isProjectBusy } from '../status'
import { DeleteProjectsDialog } from './dialogs'
import { unarchiveProjects } from './tables/projects/archiveProjects'
import { getArchivedProjectsColumns } from './tables/projects/columns'

export const ArchivedProjectsList = () => {
  const table = useRef<Table<Project> | null>(null)
  const projects = useArchivedProjects()
  const isLoading = useLoadingArchivedProjects()
  const { openDialog } = useDialogActions()
  const account = useAccount()
  useFetchArchivedProjects()

  // An unarchived or deleted row leaves the list, so its selection must not linger.
  useEffect(() => {
    table.current?.resetRowSelection()
  }, [projects])

  const selectedProjects = (selection: Record<string, boolean>) =>
    projects.filter((project) => selection[project.id])

  return (
    <>
      <DeleteProjectsDialog />

      <div className="rounded-lg border overflow-hidden">
        <div className="bg-neutral-50 border-b px-4 py-2 flex items-center gap-2">
          <Link
            to="/projects"
            aria-label="Back to all folders and projects"
            className="text-muted-foreground hover:text-foreground hover:bg-neutral-200/60 rounded p-1 cursor-pointer transition-colors"
          >
            <ArrowLeftIcon className="size-4" />
          </Link>
          <h2 className="text-sm font-semibold text-blue-7 uppercase tracking-wide">
            Archived projects
          </h2>
        </div>

        <DataTable
          columns={getArchivedProjectsColumns(account?.planKind)}
          data={projects}
          enableRowSelection={(row: Row<Project>) =>
            !isProjectBusy(row.original) &&
            isProjectDeletable(row.original, account?.planKind)
          }
          isLoading={isLoading}
          emptyMessage="You don't have any archived projects"
          height="h-auto"
          onTableReady={(value) => {
            table.current = value
          }}
          bulkActions={(selection) => (
            <>
              <Button
                theme="outline"
                size="sm"
                onClick={() => unarchiveProjects(selectedProjects(selection))}
                className="h-8 gap-1.5 text-xs font-medium"
              >
                <ArchiveRestoreIcon className="size-3.5" />
                Unarchive
              </Button>
              <Button
                theme="outline"
                size="sm"
                onClick={() =>
                  openDialog(
                    DeleteProjectsDialog.id,
                    selectedProjects(selection),
                  )
                }
                className="h-8 gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2Icon className="size-3.5" />
                Delete
              </Button>
            </>
          )}
        />
      </div>
    </>
  )
}
